import type { FormGroupSize } from '@ksyos/design-system/dist/components/form-group';
import { Alert, FormGroup, FormGroupContext, Heading, Radio, RadioGroup } from '@ksyos/design-system';
import { useMemo } from 'react';
import type { IDiaryData } from '@common/validation/holterDiary';
import { DiaryTypes, RecorderType } from '@common/enums';
import { diarySchema } from '@common/validation/holterDiary';
import { emptyValidationErrors, validate, type ValidationErrors } from '@frontend/utils';
import { HolterReadUpload } from './HolterReadUpload';

export function HolterDiaryUpload(
    props: Readonly<{
        diaryCreationFailed: boolean;
        diaryData: IDiaryData;
        errors: ValidationErrors;
        hideHeader?: boolean;
        processActivityId?: string | null;
        processId?: string | null;
        recorderType: RecorderType;
        requestId?: string | null;
        setDiaryCreationFailed: (diaryCreationFailed: boolean) => void;
        setDiaryData: (diaryData: IDiaryData) => void;
        setErrors: (errors: ValidationErrors) => void;
        setIsUploading: (isUploading: boolean) => void;
    }>
) {
    const {
        diaryCreationFailed,
        diaryData,
        errors,
        hideHeader,
        processActivityId,
        processId,
        recorderType,
        requestId,
        setDiaryCreationFailed,
        setDiaryData,
        setErrors,
        setIsUploading,
    } = props;

    const styling = useMemo(() => ({ size: 'medium' as FormGroupSize }), []);

    if (recorderType !== RecorderType.fysiologic && recorderType !== RecorderType.bittium) {
        return <Alert appearance="warning">Dit type recorder wordt niet ondersteund: {recorderType}</Alert>;
    }

    return (
        <FormGroupContext.Provider value={styling}>
            {!hideHeader && (
                <Heading
                    size="small"
                    sx={{ marginBottom: '0.75rem' }}
                >
                    Dagboek uploaden
                </Heading>
            )}
            <FormGroup
                isRequired={true}
                label="Upload het dagboek of geef aan dat het ontbreekt of dat het later wordt geüpload"
                layout="vertical"
            >
                <HolterReadUpload
                    assets={diaryData.assets}
                    errors={errors}
                    isUploading={async (isUploading) => setIsUploading(isUploading)}
                    onFilesChanged={async (files) => await handleFilesChanged(files)}
                    processActivityId={processActivityId}
                    processId={processId}
                    requestId={requestId}
                />

                <RadioGroup
                    error={
                        errors.has('diaryType')
                            ? 'Upload een dagboek of geef aan dat deze ontbreekt of later komt'
                            : undefined
                    }
                    name="paperAppOrNone"
                    onChange={async (e) => {
                        handleDiaryOptionChanged(e.target.value as DiaryTypes);
                    }}
                    value={diaryData.diaryType}
                >
                    <Radio
                        disabled={diaryData.diaryType === DiaryTypes.paperUpload}
                        label="Dagboek later uploaden"
                        value="uploadDiaryLater"
                    />
                    <Radio
                        disabled={diaryData.diaryType === DiaryTypes.paperUpload}
                        label="Dagboek ontbreekt"
                        value={DiaryTypes.none}
                    />
                </RadioGroup>
            </FormGroup>

            {diaryCreationFailed && (
                <Alert
                    appearance="warning"
                    hasIcon={true}
                >
                    Het dagboekje kon niet worden geüpload. Probeer het nog een keer. Lukt het niet? Neem dan contact op
                    met de Ksyos Servicedesk.
                </Alert>
            )}

            {diaryData.diaryType === 'uploadDiaryLater' && (
                <Alert
                    appearance="warning"
                    hasIcon={true}
                >
                    De analyse en beoordeling van het onderzoek start zodra het dagboek is toegevoegd
                </Alert>
            )}
        </FormGroupContext.Provider>
    );

    async function handleFilesChanged(assets: IDiaryData['assets']) {
        const updatedDiaryData = {
            assets,
            diaryType: assets.length ? DiaryTypes.paperUpload : undefined,
        };
        setDiaryData(updatedDiaryData);
        setErrors(validate(diarySchema, updatedDiaryData).errors);
        setDiaryCreationFailed(false);
    }

    async function handleDiaryOptionChanged(newDiaryOption: DiaryTypes | 'uploadDiaryLater') {
        const updatedDiaryData = {
            assets: diaryData.diaryType !== newDiaryOption ? [] : diaryData.assets,
            diaryType: newDiaryOption,
        };
        setDiaryData(updatedDiaryData);
        setErrors(
            newDiaryOption === 'uploadDiaryLater'
                ? emptyValidationErrors
                : validate(diarySchema, updatedDiaryData).errors
        );
        setIsUploading(false);
        setDiaryCreationFailed(false);
    }
}
